import {
    FAVORITE_MENU_KEY_PREFIX,
    FAVORITES_SECTION_PATH,
} from '@rdcomponents/navigation/favorite-routes/favorites-helper';
import {ItemType} from 'antd/es/menu/interface';
import {FC} from 'react';

import {appRoute} from '../../app-route';
import {TWO_GIS_DOMAIN} from '../../provider/domain-config/domain-config-helper';
import {DomainNameEnum} from '../../provider/domain-config/domain-config-type';
import {LicenseType} from '../../provider/license/license-provider-type';
import {getLocalizedString} from '../../provider/locale/library/localization-helper';
import {enUs} from '../../provider/locale/translation/en-us/data';
import {LangKeyType} from '../../provider/locale/translation/type';

const PATH_PREFIX_TO_OPEN_KEY: Array<{prefix: string; key: string}> = [
    {prefix: appRoute.settings.path + '/', key: appRoute.settings.path},
    {prefix: appRoute.reviewsManagement.path + '/', key: appRoute.reviewsManagement.path},
    {prefix: appRoute.reviewsAnalysis.path + '/', key: appRoute.reviewsAnalysis.path},
    {prefix: appRoute.reviewsGenerator.path + '/', key: appRoute.reviewsGenerator.path},
    {prefix: appRoute.onlinePresence.path + '/', key: appRoute.onlinePresence.path},
    {prefix: appRoute.omnichannel.path + '/', key: appRoute.omnichannel.path},
    {prefix: appRoute.competitors.path + '/', key: appRoute.competitors.path},
    {prefix: appRoute.autoRepliesRoot.path + '/', key: appRoute.autoRepliesRoot.path},
];

function normalizePathForMatch(path: string): string {
    return path.replace(/\/+$/, '') || '/';
}

export function getDefaultOpenKeys(pathname: string, favoritePaths?: Array<string>): Array<string> {
    const normalizedPathname = normalizePathForMatch(pathname);

    const isCurrentPageInFavorites =
        favoritePaths?.some((favPath) => {
            const normalizedFav = normalizePathForMatch(favPath);

            return (
                normalizedPathname === normalizedFav ||
                (normalizedFav !== '/' && normalizedPathname.startsWith(normalizedFav + '/'))
            );
        }) ?? false;

    if (isCurrentPageInFavorites) {
        return [FAVORITES_SECTION_PATH];
    }

    const matched = PATH_PREFIX_TO_OPEN_KEY.find(({prefix}) => pathname.startsWith(prefix));

    if (matched) {
        return [matched.key];
    }

    return [];
}

function stripFavoriteMenuKeyPrefix(keyString: string): string {
    if (keyString.startsWith(FAVORITE_MENU_KEY_PREFIX)) {
        return keyString.slice(FAVORITE_MENU_KEY_PREFIX.length);
    }

    return keyString;
}

function isPathnameActiveForMenuKey(pathname: string, keyString: string): boolean {
    if (!keyString) {
        return false;
    }

    const normalizedPath = normalizePathForMatch(pathname);
    const normalizedKey = normalizePathForMatch(stripFavoriteMenuKeyPrefix(keyString));

    if (normalizedPath === normalizedKey) {
        return true;
    }

    if (normalizedKey === '/') {
        return normalizedPath === '/';
    }

    return normalizedPath.startsWith(`${normalizedKey}/`);
}

function collectMatchingMenuKeys(items: Array<ItemType>, pathname: string): Array<string> {
    const matches: Array<string> = [];

    function walk(menuItems: Array<ItemType>): void {
        menuItems.forEach((item) => {
            const keyString = item?.key?.toString() || '';

            if (keyString && isPathnameActiveForMenuKey(pathname, keyString)) {
                matches.push(keyString);
            }

            if (item && 'children' in item && item.children) {
                walk(item.children);
            }
        });
    }

    walk(items);

    return matches;
}

export function getDefaultSelectedKeys(items: Array<ItemType>, pathname: string): Array<string> {
    const matches = collectMatchingMenuKeys(items, pathname);

    if (matches.length === 0) {
        return [];
    }

    const bestKey = matches.reduce((longest, current) => {
        return current.length > longest.length ? current : longest;
    });

    return [bestKey];
}

export function makeHardReload(): void {
    if (typeof window === 'undefined') {
        return;
    }

    window.location.reload();
}

export function isEnabledForDomainAndDoubleGis(options: {
    isDomainFeatureEnabled: boolean;
    isFeatureEnabled: boolean;
    domainName: DomainNameEnum | string;
}): boolean {
    if (options.domainName === DomainNameEnum.twoGis) {
        return options.isDomainFeatureEnabled && options.isFeatureEnabled;
    }

    return options.isDomainFeatureEnabled;
}

export function isEnabledForDomainAndDoubleGisWhiteLabel(options: {
    isDomainFeatureEnabled: boolean;
    isFeatureEnabled: boolean;
    domainName: DomainNameEnum | string;
}): boolean {
    const isDoubleGisWhiteLabel = options.domainName.includes(TWO_GIS_DOMAIN);

    if (isDoubleGisWhiteLabel) {
        return options.isDomainFeatureEnabled && options.isFeatureEnabled;
    }

    return options.isDomainFeatureEnabled;
}

export function getFeatureOrLandingPage(options: {
    license?: LicenseType;
    activationComponent: FC<Record<string, unknown>>;
    pageComponent: FC<Record<string, unknown>>;
}): FC<Record<string, unknown>> {
    const {license, activationComponent, pageComponent} = options;

    if (!license || (!license.isActive && license.canActivateTrial)) {
        return activationComponent;
    }

    return pageComponent;
}

export function getMenuItemNameForAnalytics(langKey: LangKeyType, valueMap?: Record<string, string>): string {
    return getLocalizedString(langKey, enUs, valueMap);
}
